import { Account } from '@/types/accounts'
import { Customer } from '@/types/customers'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import buildService from '../create-service'
import queryKeys from '../query-keys'
import { createDetail } from '../request-wrappers'

const service = buildService<Account>({
  pathCategory: 'accounts',
})

export function useGetAccount({
  id,
  ...options
}: UseGetDetailOptions<Account>) {
  const query = useGetDetail<Account>({
    id,
    queryKey: queryKeys.accounts.detail({ id, filters: options.filters }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    account: query.data,
    ...query,
  }
}

export function useGetAccounts({
  filters,
  ...options
}: UseGetListOptions<Account> = {}) {
  const query = useGetList<Account>({
    filters,
    queryKey: queryKeys.accounts.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    accounts: query.allData,
    ...query,
  }
}

export function useUpdateAccount(
  options: UseUpdateDetailOptions<Account> = {},
) {
  const mutation = useUpdateDetail<Account>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateAccount: mutation.mutate,
    updateAccountAsync: mutation.mutateAsync,
    ...mutation,
  }
}

type CreateAccount = Pick<Account, 'id' | 'name'> & {
  customer?: Omit<Customer, 'owner_org_id' | 'account_id'>
}

/**
 * MARK: Action Hook: Create Account with Defaults
 * Creates a new Account with default values.
 */
export function useCreateAccountWithDefaults(
  options: UseCreateDetailOptions<CreateAccount, Account> = {},
) {
  const mutation = useCreateDetail<CreateAccount, Account>({
    serviceFn: (serviceFnOptions) => {
      return createDetail({
        url: '/v2/pd/accounts/create-with-defaults',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    createAccount: mutation.mutate,
    createAccountAsync: mutation.mutateAsync,
    ...mutation,
  }
}
